import {ChangeDetectorRef, Component, OnInit} from '@angular/core'
import {Router} from '@angular/router'
import {FormBuilder, FormGroup, Validators} from '@angular/forms'
import {throwError} from 'rxjs'
import {AuthenticationService} from '@app/core/services/authentication.service'
import {AuthHelperService} from '@app/core/services/auth-helper.service'
import {
  SsoAuthService,
  SsoLoginReason,
} from '@app/core/services/sso-auth.service'
import {finalize, tap, catchError, first} from 'rxjs/operators'
import {ContextService} from '@app/core/services/context.service'
import {
  SsoLoginError,
  consumeSsoLoginError,
  resetSsoRetryAttempts,
  takeSsoRetryAttempt,
} from '@app/core/util/sso-login-error.util'
import {resolveLocalLogin} from '@app/core/util/local-login.util'
import {LogoutCoordinatorService} from '@app/core/services/logout-coordinator.service'
import {AppConfigService} from '@app/app-config.service'

@Component({
  templateUrl: 'login.component.html',
  standalone: false,
})
export class LoginComponent implements OnInit {
  isPasswordHidden = true
  remembered: {username?: string; domain?: string} | null
  form: FormGroup
  loading = false
  connectionError = false
  /** Pending Keycloak/SSO failure, shown in its own alert. Kept OFF the form:
   *  `form.setErrors` set before the form directives bind is wiped when each
   *  `formControlName` registration re-runs the group's validity. */
  ssoError: SsoLoginError | null = null
  /** A completed logout lands here without immediately bouncing back to SSO. */
  justLoggedOut = false
  /** True once we've decided to render the classic username/password form.
   *  While false the entry decision is still in flight (or we're mid-redirect
   *  to Keycloak) and the template shows a loader instead. */
  showLocalForm = false
  /** Emergency override (cookie / `?local=` param) that forces the classic form
   *  even when SSO is configured — see {@link resolveLocalLogin}. */
  private forceLocalLogin = false
  /** Fixed reason code forwarded to the Keycloak theme on forced re-entry. */
  private ssoLoginReason: SsoLoginReason | undefined
  constructor(
    private fb: FormBuilder,
    private router: Router,
    private authService: AuthenticationService,
    private helper: AuthHelperService,
    private context: ContextService,
    protected sso: SsoAuthService,
    private cdr: ChangeDetectorRef,
    private logoutCoordinator: LogoutCoordinatorService,
    private appConfig: AppConfigService,
  ) {}

  get production(): boolean {
    return this.appConfig.production
  }

  get ssoMigrationDate(): string | null {
    return this.appConfig.ssoMigrationDate
  }

  /** Show the Keycloak/SSO button when SSO is configured — a BFF `ssoLoginUrl`,
   *  or an authorization endpoint + client id for the direct code+PKCE flow. */
  get ssoEnabled(): boolean {
    return this.sso.isEnabled
  }

  loginWithSso() {
    // A deliberate attempt refills the silent-retry budget.
    resetSsoRetryAttempts()
    // Async (PKCE challenge generation) then a full-page redirect — fire and forget.
    void this.sso.startLogin()
  }

  ngOnInit() {
    // retrieve the last known valid login
    this.remembered = this.authService.rememberedCredentials as {
      username?: string
      domain?: string
    } | null
    this.form = this.fb.group({
      username: this.fb.control(
        this.remembered ? this.remembered.username : null,
        Validators.required,
      ),
      password: this.fb.control(null, Validators.required),
      domain: this.fb.control(
        this.remembered ? this.remembered.domain : null,
        Validators.required,
      ),
    })

    // Read any pending SSO failure ONCE — it doubles as the loop guard in
    // decideEntry(): after a failed Keycloak attempt we must NOT auto-bounce
    // straight back to Keycloak, or a rejected account loops forever.
    this.ssoError = consumeSsoLoginError()
    const automaticLoginSource =
      this.logoutCoordinator.consumeAutomaticLoginSuppression()
    this.justLoggedOut = automaticLoginSource !== null
    this.ssoLoginReason =
      automaticLoginSource === 'refresh-failure' ? 'session_expired' : undefined
    this.forceLocalLogin = resolveLocalLogin()

    this.authService
      .isAuthenticated()
      .pipe(first())
      .subscribe({
        next: (authed) => this.decideEntry(authed),
        // If the auth check itself errors, fall back to the classic form so the
        // user is never stranded on a blank loader.
        error: () => this.renderForm(),
      })
  }

  /**
   * Decide what a portal entry does:
   *  - already authenticated        → straight into the app;
   *  - a retryable SSO failure       → silently restart the Keycloak flow
   *    (fresh state/PKCE; the SSO cookie usually completes it without any
   *    interaction), capped by a per-tab budget so a persistent failure
   *    cannot redirect-loop;
   *  - a terminal SSO failure, or the budget spent → Keycloak's own login
   *    page with `prompt=login` + the `login_failed` reason (the theme owns
   *    the message; forced interaction there rules out a silent loop);
   *  - SSO configured & no override  → redirect to Keycloak;
   *  - otherwise (SSO off, or the `?local=1` escape hatch) → classic form.
   */
  private decideEntry(authed: boolean): void {
    if (authed) {
      resetSsoRetryAttempts()
      this.router.navigate([this.helper.location || '/dashboard'])
      return
    }
    if (this.ssoError) {
      if (this.sso.isEnabled && !this.forceLocalLogin) {
        if (this.ssoError.retryable && takeSsoRetryAttempt()) {
          // A fresh round-trip usually heals it, silently (the Keycloak SSO
          // cookie completes the flow without any interaction).
          void this.sso.startLogin(false, 'login_retry')
        } else {
          /* Terminal failure, or the silent budget is spent. The legacy form
             stays hidden regardless — the user goes to Keycloak's own login
             page instead, where the theme owns the `login_failed` message.
             `prompt=login` forces interaction there, so a persistent failure
             becomes a visible stop on Keycloak, never a silent redirect
             loop. The classic form remains reachable only through the
             `?local=1` escape hatch. */
          void this.sso.startLogin(true, 'login_failed')
        }
        return
      }
      this.renderForm()
      return
    }
    if (this.justLoggedOut && !this.sso.isEnabled) {
      this.renderForm()
      return
    }
    if (this.sso.isEnabled && !this.forceLocalLogin) {
      void this.sso.startLogin(this.justLoggedOut, this.ssoLoginReason)
      return
    }
    this.renderForm()
  }

  private renderForm(): void {
    this.showLocalForm = true
    // Zoneless: this fires from an async subscription, so schedule CD explicitly.
    this.cdr.markForCheck()
  }

  togglePasswordHideShow() {
    this.isPasswordHidden = !this.isPasswordHidden
  }

  submit() {
    // start loading indicator
    this.loading = true
    const values = this.form.value
    // attempt to authenticate
    this.connectionError = false
    this.authService
      .logIn(values.username, values.password, values.domain, true)
      .pipe(
        first(),
        tap(() => {
          let url = this.helper.location
          if (!url) {
            url = ''
          }
          // set initial rootdomain in the session
          this.context.changeActiveDomain(values.domain)
          // redirect to the specified url
          this.router.navigate([url])
        }),
        catchError((err) => {
          let errMsg = 'Failed to authenticate. Please check your credentials.'
          if (err.error?.description === 'account blocked') {
            errMsg =
              'There have been a number of failed login attempts. The account has been blocked for 15 minutes. Please try again later or reset your password.'
          }
          if (err.error?.description === 'account disabled') {
            errMsg =
              'This user account is deactivated. This user account no longer has access to MyPayter. Please contact your admin to reactivate your access.'
          }
          if (err.status !== 400) {
            errMsg =
              'The MyPayter Portal is currently unavailable. Please try again later or contact your account manager.'
            this.connectionError = true
          }
          this.form.setErrors({global: errMsg})
          return throwError(err)
        }),
        finalize(() => {
          // end loading indicator
          this.loading = false
          // Zoneless: nothing else schedules change detection after the async
          // login settles, so a failed login would otherwise leave the spinner
          // stuck and the error hidden until the next interaction.
          this.cdr.markForCheck()
        }),
      )
      .subscribe()
  }

  showFieldInvalid(name: string) {
    const field = this.form.get(name)
    return field.touched && field.invalid
  }
}
